import React, {useContext, useEffect, useMemo, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {AuthContext} from "../../../config/AuthContext";
import NavBar from "../../components/NavBar";
import Login from "../../root/Login";
import {Button, Card, InputNumber, Select, Table, Tag, Tooltip, Space} from "antd";
import {ColumnsType, TableProps} from "antd/es/table";
import Colors from "../../../styles/Colors";
import {BiPlus, BiSearch, BiTrash} from "react-icons/bi";
import {BsEnvelope, BsEnvelopePaper} from "react-icons/bs";
import Historials from "../../components/Historials";
import {GetEscoles} from "../../../dao/root/GetEscoles";
import {Escola} from "../../../model/root/Escola";
import {GetEmailsActivitats} from "../../../dao/multilleure/extraescolars/GetEmailsActivitats";
import {GetEmailsPerActivitat, GetEmailsPerEscola, GetEmailsMultiples} from "../../../dao/multilleure/extraescolars/GetEmails";
import toast, {Toaster} from "react-hot-toast";

interface AlumneEmail {
    correu: string;
    nomNen: string;
    nomContacte: string;
}

interface ActivitatEmail {
    idacti: number;
    nomweb: string;
}

// Any escolar per defecte: si estem abans d'agost, el curs és l'any anterior
function anyEscolarActual(): number {
    const now = new Date();
    return now.getMonth() <= 7 ? now.getFullYear() - 1 : now.getFullYear();
}

const TOTES = 'TOTES';

const Emails = () => {
    const titol = "Consulta e-mails";
    const {isLoggedIn, page} = useContext(AuthContext);

    const [any, setAny] = useState<number>(anyEscolarActual());
    // escoles: llista {value: id_loop, label: nom} — mateix origen que Beques
    const [escoles, setEscoles] = useState<{ value: string; label: string }[]>([]);
    const [escola, setEscola] = useState<string>(TOTES); // guardem l'id_loop
    const [activitats, setActivitats] = useState<ActivitatEmail[]>([]);
    const [activitatSel, setActivitatSel] = useState<number | undefined>();
    const [seleccionades, setSeleccionades] = useState<ActivitatEmail[]>([]);
    const [alumnes, setAlumnes] = useState<AlumneEmail[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isLoggedIn) carregarEscoles();
    }, [isLoggedIn]);

    // En canviar any o escola, recarreguem les activitats disponibles
    useEffect(() => {
        if (isLoggedIn && escola !== TOTES) carregarActivitats();
        else setActivitats([]);
    }, [any, escola, isLoggedIn]);

    async function carregarEscoles() {
        const res = await GetEscoles(page);
        const list = (res || []).map((e: Escola) => ({
            value: e.id_loop,
            label: e.nom,
        }));
        setEscoles(list);
    }

    async function carregarActivitats() {
        const res = await GetEmailsActivitats(page, String(any), escola);
        setActivitats(res);
    }

    // Afegeix l'activitat seleccionada a la llista acumulada
    function afegirActivitat() {
        if (!activitatSel) return;
        const act = activitats.find(a => a.idacti === activitatSel);
        if (!act) return;
        if (seleccionades.some(s => s.idacti === act.idacti)) {
            toast.error("Aquesta activitat ja està afegida");
            return;
        }
        setSeleccionades(prev => [...prev, act]);
    }

    function esborrarSeleccionades() {
        setSeleccionades([]);
    }

    async function cercar() {
        setLoading(true);
        try {
            // Cas 1: múltiples activitats acumulades
            if (seleccionades.length > 0) {
                const ids = seleccionades.map(a => a.idacti).join(',');
                setAlumnes(await GetEmailsMultiples(page, ids));
                return;
            }
            // Cas 2: una activitat seleccionada
            if (activitatSel) {
                setAlumnes(await GetEmailsPerActivitat(page, String(activitatSel)));
                return;
            }
            // Cas 3: escola concreta sense activitat → tots els alumnes de l'escola
            if (escola !== TOTES) {
                setAlumnes(await GetEmailsPerEscola(page, escola));
                return;
            }
            toast.error("Selecciona una escola i/o activitat");
            setAlumnes([]);
        } finally {
            setLoading(false);
        }
    }

    // Tots els emails únics per al "enviar a tots"
    const totsEmails = useMemo(() => {
        const set = new Set(alumnes.map(a => a.correu).filter(c => c && c.trim() !== ''));
        return Array.from(set).join(',');
    }, [alumnes]);

    const columns: ColumnsType<AlumneEmail> = [
        {key: 'nomNen', title: 'Nom nen', dataIndex: 'nomNen'},
        {key: 'nomContacte', title: 'Nom contacte', dataIndex: 'nomContacte'},
        {
            key: 'correu', title: 'Email', dataIndex: 'correu',
            render: (v) => v ? <Tag color={Colors.colors.color4}>{v}</Tag> : <Tag>—</Tag>
        },
        {
            key: 'accions', title: 'Accions', dataIndex: 'accions', width: 160,
            render: (_, ele) => (
                ele.correu ? (
                    <Button
                        size="small"
                        icon={<BsEnvelope/>}
                        style={{backgroundColor: Colors.colors.color2, color: '#fff'}}
                        href={`mailto:${ele.correu}`}
                    >
                        Enviar
                    </Button>
                ) : null
            )
        },
    ];

    const tableProps: TableProps<AlumneEmail> = {loading};

    if (!isLoggedIn) return <Login/>;

    return (
        <div>
            <Toaster/>
            <NavBar/>
            <div className="general">
                <Card bordered className="elemento">
                    <h4 className="titol" style={{marginBottom: 16}}>{titol}</h4>

                    {/* Filtres en una sola fila */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                            marginBottom: 16,
                            width: '100%',
                        }}>
                        <span>Any:</span>
                        <InputNumber
                            min={2000}
                            max={3000}
                            value={any}
                            onChange={(v) => v && setAny(v)}
                            style={{width: 100}}
                        />

                        <span>Escola:</span>
                        <Select
                            showSearch
                            value={escola}
                            onChange={(v) => {
                                setEscola(v);
                                setActivitatSel(undefined);
                                setSeleccionades([]);
                            }}
                            optionFilterProp="label"
                            style={{width: 240}}
                            options={[
                                {value: TOTES, label: 'TOTES'},
                                ...escoles,
                            ]}
                        />

                        {/* Activitat: només visible si hi ha escola concreta */}
                        {escola !== TOTES && (
                            <>
                                <span>Activitat:</span>
                                <Select
                                    showSearch
                                    placeholder="Selecciona..."
                                    value={activitatSel}
                                    onChange={setActivitatSel}
                                    optionFilterProp="label"
                                    style={{width: 240}}
                                    options={activitats.map(a => ({value: a.idacti, label: a.nomweb}))}
                                />
                                <Tooltip title="Afegir activitat a la llista">
                                    <Button
                                        icon={<BiPlus/>}
                                        style={{backgroundColor: Colors.colors.color5}}
                                        onClick={afegirActivitat}
                                    />
                                </Tooltip>
                                <Tooltip title="Buidar llista d'activitats">
                                    <Button
                                        icon={<BiTrash/>}
                                        danger
                                        onClick={esborrarSeleccionades}
                                    />
                                </Tooltip>
                            </>
                        )}

                        <Tooltip title="Cercar emails">
                            <Button
                                type="primary"
                                icon={<BiSearch/>}
                                style={{backgroundColor: Colors.colors.color1}}
                                loading={loading}
                                onClick={cercar}
                            />
                        </Tooltip>
                    </div>

                    {/* Llista d'activitats acumulades */}
                    {seleccionades.length > 0 && (
                        <div style={{margin: '8px 0'}}>
                            <Space wrap>
                                {seleccionades.map(a => (
                                    <Tag
                                        key={a.idacti}
                                        color={Colors.colors.color3}
                                        closable
                                        onClose={() => setSeleccionades(prev => prev.filter(s => s.idacti !== a.idacti))}
                                    >
                                        {a.nomweb}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    )}

                    <Table
                        {...tableProps}
                        rowKey={(r) => `${r.correu}-${r.nomNen}-${r.nomContacte}`}
                        columns={columns}
                        dataSource={alumnes}
                        pagination={{pageSize: 50}}
                        locale={{emptyText: "Selecciona escola i/o activitat i prem cercar."}}
                        footer={() =>
                            alumnes.length > 0 && totsEmails ? (
                                <Button
                                    icon={<BsEnvelopePaper/>}
                                    style={{backgroundColor: Colors.colors.color1, color: '#fff'}}
                                    href={`mailto:?bcc=${totsEmails}`}
                                >
                                    Enviar e-mail a tots
                                </Button>
                            ) : null
                        }
                    />
                </Card>
                <br/>
                <Historials pantalla="EMAILS" pagina={page}/>
            </div>
        </div>
    );
};

export default Emails;
